import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  RefreshCw,
  Download,
  Search,
  User,
  Terminal,
  Info,
  AlertTriangle,
  XCircle,
  Flame,
  Calendar,
  Clock,
  Copy,
  ChevronDown,
  ChevronUp,
  ChevronsDown,
  FileText,
  Layers,
  ArrowDownAZ,
  ArrowUpAZ,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { getLogDates, getLogs, downloadLogFile } from "../../Api_Qdrant/Logs_Api";
import { routePath as RP } from "../../app/routes/routepath";
import "./Logs.css";

const MODE_STORAGE_KEY = "tfLogsViewerMode";
const FILTERS_VISIBLE_KEY = "tfLogsFiltersVisible";

// level_type is always one of these three, regardless of the raw level
// (CRITICAL folds into "problem" alongside ERROR). Used to color/badge any
// event coming back from GET /logs, in either mode.
const LEVEL_TYPE_META = {
  problem: { color: "#e5484d" },
  warning: { color: "#e5a000" },
  info: { color: "#2f7de1" },
};

const levelColor = (levelType) => LEVEL_TYPE_META[levelType]?.color || "#8a8f98";

// The raw `level` query param Developer Mode filters on - independent of
// level_type (this splits "problem" back into error vs critical).
const RAW_LEVELS = [
  { key: "info", label: "Info", color: "#2f7de1", Icon: Info },
  { key: "warning", label: "Warning", color: "#e5a000", Icon: AlertTriangle },
  { key: "error", label: "Error", color: "#e5484d", Icon: XCircle },
  { key: "critical", label: "Critical", color: "#b91c1c", Icon: Flame },
];

const humanizeKey = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// technical_details is a flexible bag (component/source_file always present,
// extra_info/stack_trace/additional_lines only when relevant) - render
// whatever keys actually came back instead of hardcoding field names.
const formatTechnicalDetails = (details) => {
  if (!details || typeof details !== "object") return "";
  return Object.entries(details)
    .map(([key, value]) => `${humanizeKey(key)}:\n${value}`)
    .join("\n\n");
};

function toDate(timestamp) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatPreciseTime(timestamp) {
  const date = toDate(timestamp);
  if (!date) return timestamp || "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const STATUS_META = {
  ok: { label: "All Good", color: "#16a34a", bg: "rgba(22, 163, 74, 0.08)" },
  warning: { label: "Needs Attention", color: "#d97706", bg: "rgba(217, 119, 6, 0.08)" },
  problem: { label: "Problems Found", color: "#dc2626", bg: "rgba(220, 38, 38, 0.08)" },
};

function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function Logs() {
  const navigate = useNavigate();

  const [mode, setMode] = useState(() => localStorage.getItem(MODE_STORAGE_KEY) || "user");
  const isDeveloper = mode === "developer";

  // Collapses the filter controls so the log list gets more room - remembers
  // the user's last choice.
  const [filtersVisible, setFiltersVisible] = useState(
    () => localStorage.getItem(FILTERS_VISIBLE_KEY) !== "false"
  );
  useEffect(() => {
    localStorage.setItem(FILTERS_VISIBLE_KEY, String(filtersVisible));
  }, [filtersVisible]);

  /* ---- dates (shared) ---- */
  const [dateEntries, setDateEntries] = useState([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const [datesError, setDatesError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  /* ---- shared filters ---- */
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  const [expandedId, setExpandedId] = useState(null);

  /* ---- user mode: simple, local view toggle over problems/warnings ---- */
  const [userLevelFilter, setUserLevelFilter] = useState(null); // null | "problem" | "warning"

  /* ---- developer mode: real server-side filters ---- */
  const [devLevels, setDevLevels] = useState(() => new Set()); // empty = all
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [componentInput, setComponentInput] = useState("");
  const debouncedComponent = useDebouncedValue(componentInput, 400);
  const [sort, setSort] = useState("desc");
  const [devLimit, setDevLimit] = useState(200);
  const [downloadType, setDownloadType] = useState("app");
  const [downloading, setDownloading] = useState(false);

  /* ---- shared fetched data (one endpoint, both modes) ---- */
  const [statusInfo, setStatusInfo] = useState(null);
  const [summary, setSummary] = useState(null);
  const [problems, setProblems] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [mode]);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setExpandedId(null);
  };

  // Load the list of dates (and which raw files exist per date, for the
  // download picker) once on mount.
  useEffect(() => {
    (async () => {
      setDatesLoading(true);
      const res = await getLogDates();
      if (res?.success) {
        const dates = res.data?.dates || [];
        setDateEntries(dates);
        if (dates.length > 0) setSelectedDate(dates[0].date);
      } else {
        setDatesError(res?.message || "Could not load the list of available log dates.");
      }
      setDatesLoading(false);
    })();
  }, []);

  const availableTypes = useMemo(
    () => dateEntries.find((d) => d.date === selectedDate)?.types || [],
    [dateEntries, selectedDate]
  );

  useEffect(() => {
    if (availableTypes.length > 0 && !availableTypes.includes(downloadType)) {
      setDownloadType(availableTypes.includes("app") ? "app" : availableTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTypes]);

  /* ---- the one shared fetch ---- */

  const buildParams = useCallback(
    (offsetOverride) => ({
      date: selectedDate,
      search: debouncedSearch.trim() || undefined,
      sort: isDeveloper ? sort : "desc",
      // User Mode never reads `entries`, so ask for the smallest page - the
      // day-wide problems/warnings/summary are unaffected by limit/offset.
      limit: isDeveloper ? devLimit : 1,
      offset: isDeveloper ? offsetOverride : 0,
      startTime: isDeveloper ? startTime || undefined : undefined,
      endTime: isDeveloper ? endTime || undefined : undefined,
      component: isDeveloper ? debouncedComponent.trim() || undefined : undefined,
      levels: isDeveloper && devLevels.size > 0 ? [...devLevels] : undefined,
    }),
    [
      selectedDate,
      debouncedSearch,
      isDeveloper,
      sort,
      devLimit,
      startTime,
      endTime,
      debouncedComponent,
      devLevels,
    ]
  );

  const fetchLogs = useCallback(
    async (offsetOverride = 0, append = false) => {
      if (!selectedDate) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError("");

      const res = await getLogs(buildParams(offsetOverride));
      if (res?.success) {
        const data = res.data || {};
        setStatusInfo({ status: data.status, message: data.status_message });
        setSummary(data.summary || null);
        setProblems(data.problems || []);
        setWarnings(data.warnings || []);
        setEntries((prev) => (append ? [...prev, ...(data.entries || [])] : data.entries || []));
        setPagination(data.pagination || null);
        setOffset(offsetOverride);
      } else {
        if (!append) {
          setStatusInfo(null);
          setSummary(null);
          setProblems([]);
          setWarnings([]);
          setEntries([]);
          setPagination(null);
        }
        setError(res?.message || "Could not load logs for this date.");
      }
      if (append) setLoadingMore(false);
      else setLoading(false);
    },
    [selectedDate, buildParams]
  );

  useEffect(() => {
    fetchLogs(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, isDeveloper, debouncedSearch, sort, devLimit, startTime, endTime, debouncedComponent, devLevels]);

  const handleRefresh = () => fetchLogs(0, false);

  const handleLoadMore = () => {
    if (!pagination?.has_more) return;
    fetchLogs(offset + devLimit, true);
  };

  const handleDownload = async () => {
    if (!selectedDate) return;
    setDownloading(true);
    const res = await downloadLogFile({ date: selectedDate, type: downloadType });
    if (!res.success) {
      toast.error(res.message || "Could not download the log file.");
    }
    setDownloading(false);
  };

  const handleCopy = async (text, successMessage = "Copied to clipboard") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      toast.error("Could not copy — your browser blocked clipboard access.");
    }
  };

  const toggleDevLevel = (key) => {
    setDevLevels((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ---- user mode: merge problems + warnings into one timeline ---- */

  const userEvents = useMemo(() => {
    const merged = [...problems, ...warnings].map((item, idx) => ({ ...item, _id: idx }));
    merged.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
    return merged;
  }, [problems, warnings]);

  const filteredUserEvents = useMemo(() => {
    if (!userLevelFilter) return userEvents;
    return userEvents.filter((item) => item.level_type === userLevelFilter);
  }, [userEvents, userLevelFilter]);

  const statusMeta = STATUS_META[(statusInfo?.status || "").toLowerCase()] || {
    label: statusInfo?.status || "Status Unknown",
    color: "#6b7280",
    bg: "rgba(107, 114, 128, 0.08)",
  };

  return (
    <div className={`tfl-page ${isDeveloper ? "tfl-dark" : "tfl-light"}`}>
      <div className="tfl-topbar">
        <button className="tfl-back-btn" onClick={() => navigate(RP.showNewDashboard)}>
          <ArrowLeft size={16} />
          <span>Dashboard</span>
        </button>

        <div className="tfl-title-block">
          <h1>System Logs</h1>
          <p>
            {isDeveloper
              ? "Search and filter raw application logs."
              : "A simple view of what the system has been doing."}
          </p>
        </div>

        <div className="tfl-mode-toggle" role="tablist" aria-label="Log viewer mode">
          <button
            className={!isDeveloper ? "tfl-mode-active" : ""}
            onClick={() => handleModeChange("user")}
            role="tab"
            aria-selected={!isDeveloper}
          >
            <User size={14} />
            <span>User Mode</span>
          </button>
          <button
            className={isDeveloper ? "tfl-mode-active" : ""}
            onClick={() => handleModeChange("developer")}
            role="tab"
            aria-selected={isDeveloper}
          >
            <Terminal size={14} />
            <span>Developer Mode</span>
          </button>
        </div>

        <button
          className={`tfl-filters-toggle-btn ${filtersVisible ? "tfl-filters-toggle-active" : ""}`}
          onClick={() => setFiltersVisible((v) => !v)}
          title={filtersVisible ? "Hide filters to see more logs" : "Show filters"}
        >
          <SlidersHorizontal size={14} />
          <span>Filters</span>
          {filtersVisible ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {datesError && <div className="tfl-banner tfl-banner-error">{datesError}</div>}

      {!datesLoading && dateEntries.length === 0 && !datesError && (
        <div className="tfl-empty-state">
          <FileText size={32} />
          <p>No log files are available yet.</p>
        </div>
      )}

      {(datesLoading || dateEntries.length > 0) && (
        <>
          <div className="tfl-controls tfl-controls-essential">
            <div className="tfl-control">
              <label>
                <Calendar size={13} /> Date
              </label>
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={datesLoading}
              >
                {dateEntries.map((entry) => (
                  <option key={entry.date} value={entry.date}>
                    {entry.date}
                  </option>
                ))}
              </select>
            </div>

            <button className="tfl-icon-btn" onClick={handleRefresh} disabled={loading} title="Refresh">
              <RefreshCw size={15} className={loading ? "tfl-spin" : ""} />
            </button>
          </div>

          {filtersVisible && (
            <div className="tfl-controls">
              {isDeveloper && (
                <>
                  <div className="tfl-control">
                    <label>
                      <Clock size={13} /> From
                    </label>
                    <input type="time" step="1" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                  </div>
                  <div className="tfl-control">
                    <label>
                      <Clock size={13} /> To
                    </label>
                    <input type="time" step="1" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                  </div>
                  <div className="tfl-control">
                    <label>Component</label>
                    <input
                      type="text"
                      className="tfl-text-input"
                      placeholder="e.g. chat_service"
                      value={componentInput}
                      onChange={(e) => setComponentInput(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="tfl-control tfl-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder={isDeveloper ? "Search message text…" : "Search…"}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button className="tfl-clear-search" onClick={() => setSearchInput("")}>
                    <X size={13} />
                  </button>
                )}
              </div>

              {isDeveloper && (
                <>
                  <div className="tfl-control">
                    <label>Sort</label>
                    <button
                      className="tfl-icon-btn"
                      onClick={() => setSort(sort === "desc" ? "asc" : "desc")}
                      title="Toggle sort order"
                    >
                      {sort === "desc" ? <ArrowDownAZ size={14} /> : <ArrowUpAZ size={14} />}
                      {sort === "desc" ? "Newest first" : "Oldest first"}
                    </button>
                  </div>

                  <div className="tfl-control">
                    <label>Page size</label>
                    <select value={devLimit} onChange={(e) => setDevLimit(Number(e.target.value))}>
                      {[50, 100, 200, 500, 1000].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="tfl-control">
                    <label>File</label>
                    <select value={downloadType} onChange={(e) => setDownloadType(e.target.value)}>
                      {(availableTypes.length > 0 ? availableTypes : ["app"]).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="tfl-icon-btn tfl-download-btn"
                    onClick={handleDownload}
                    disabled={downloading || !selectedDate}
                    title="Download raw .log file"
                  >
                    <Download size={15} className={downloading ? "tfl-spin" : ""} />
                    <span>Download</span>
                  </button>
                </>
              )}
            </div>
          )}

          {filtersVisible && isDeveloper && (
            <div className="tfl-stats">
              <button
                className={`tfl-stat-chip ${devLevels.size === 0 ? "tfl-stat-active" : ""}`}
                style={{ "--chip-color": "#6b7280" }}
                onClick={() => setDevLevels(new Set())}
              >
                <Layers size={16} />
                <span className="tfl-stat-label">All</span>
              </button>
              {RAW_LEVELS.map(({ key, label, color, Icon }) => {
                const active = devLevels.has(key);
                return (
                  <button
                    key={key}
                    className={`tfl-stat-chip ${active ? "tfl-stat-active" : ""}`}
                    style={{ "--chip-color": color }}
                    onClick={() => toggleDevLevel(key)}
                  >
                    <Icon size={16} />
                    <span className="tfl-stat-label">{label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && !error && summary && (
            <div className="tfl-stats tfl-stats-readonly">
              <div className="tfl-stat-card">
                <span className="tfl-stat-count">{summary.total_matching ?? 0}</span>
                <span className="tfl-stat-label">Total Events</span>
              </div>
              <div className="tfl-stat-card" style={{ "--chip-color": "#e5484d" }}>
                <span className="tfl-stat-count">{summary.problems ?? 0}</span>
                <span className="tfl-stat-label">Problems</span>
              </div>
              <div className="tfl-stat-card" style={{ "--chip-color": "#e5a000" }}>
                <span className="tfl-stat-count">{summary.warnings ?? 0}</span>
                <span className="tfl-stat-label">Warnings</span>
              </div>
              <div className="tfl-stat-card" style={{ "--chip-color": "#16a34a" }}>
                <span className="tfl-stat-count">{summary.normal_events ?? 0}</span>
                <span className="tfl-stat-label">Normal</span>
              </div>
            </div>
          )}

          {filtersVisible && !isDeveloper && !loading && !error && userEvents.length > 0 && (
            <div className="tfl-user-filter-row">
              <button
                className={`tfl-user-filter-chip ${!userLevelFilter ? "tfl-stat-active" : ""}`}
                style={{ "--chip-color": "#6b7280" }}
                onClick={() => setUserLevelFilter(null)}
              >
                All <span className="tfl-chip-count">{userEvents.length}</span>
              </button>
              <button
                className={`tfl-user-filter-chip ${userLevelFilter === "problem" ? "tfl-stat-active" : ""}`}
                style={{ "--chip-color": levelColor("problem") }}
                onClick={() => setUserLevelFilter(userLevelFilter === "problem" ? null : "problem")}
              >
                Problem <span className="tfl-chip-count">{problems.length}</span>
              </button>
              <button
                className={`tfl-user-filter-chip ${userLevelFilter === "warning" ? "tfl-stat-active" : ""}`}
                style={{ "--chip-color": levelColor("warning") }}
                onClick={() => setUserLevelFilter(userLevelFilter === "warning" ? null : "warning")}
              >
                Warning <span className="tfl-chip-count">{warnings.length}</span>
              </button>
            </div>
          )}

          {!isDeveloper && statusInfo && !loading && !error && (
            <div
              className="tfl-status-banner"
              style={{ "--status-color": statusMeta.color, "--status-bg": statusMeta.bg }}
            >
              <span className="tfl-status-badge">{statusMeta.label}</span>
              <p>{statusInfo.message}</p>
            </div>
          )}

          {isDeveloper && pagination && (
            <div className="tfl-meta-line">
              Showing {entries.length.toLocaleString()} of {pagination.total_matching?.toLocaleString()} matching
              events
            </div>
          )}

          <div className="tfl-log-panel">
            {loading && (
              <div className="tfl-loading">
                <div className="tfl-spinner" />
                <p>Loading logs…</p>
              </div>
            )}

            {!loading && error && <div className="tfl-banner tfl-banner-error">{error}</div>}

            {!loading &&
              !error &&
              (isDeveloper ? (
                entries.length === 0 ? (
                  <div className="tfl-empty-state">
                    <FileText size={28} />
                    <p>No log events match these filters.</p>
                  </div>
                ) : (
                  <>
                    <div className="tfl-dev-list">
                      {entries.map((line, idx) => {
                        const expanded = expandedId === idx;
                        const toggle = () => setExpandedId(expanded ? null : idx);
                        const color = levelColor(line.level_type);
                        const detailsText = formatTechnicalDetails(line.technical_details);
                        return (
                          <div
                            key={idx}
                            className={`tfl-dev-row ${expanded ? "tfl-dev-row-expanded" : ""}`}
                            style={{ "--chip-color": color }}
                          >
                            <span className="tfl-dev-badge" style={{ "--chip-color": color }}>
                              {line.level || "LOG"}
                            </span>
                            <span className="tfl-dev-time">{formatPreciseTime(line.timestamp)}</span>
                            <span className="tfl-dev-logger">{line.technical_details?.component}</span>
                            <span className="tfl-dev-message" onClick={toggle} title="Click to view the full log">
                              {line.message}
                            </span>
                            <div className="tfl-dev-actions">
                              <button
                                className="tfl-mini-btn"
                                onClick={toggle}
                                title={expanded ? "Collapse" : "View full log"}
                              >
                                {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                              </button>
                              <button
                                className="tfl-mini-btn"
                                onClick={() =>
                                  handleCopy(`${line.timestamp} | ${line.level} | ${line.message}`, "Log line copied")
                                }
                                title="Copy line"
                              >
                                <Copy size={13} />
                              </button>
                            </div>
                            {expanded && (
                              <div className="tfl-dev-expanded">
                                <div className="tfl-dev-expanded-block">
                                  <span className="tfl-dev-expanded-label">Message</span>
                                  <pre>{line.message}</pre>
                                </div>
                                {detailsText && (
                                  <div className="tfl-dev-expanded-block">
                                    <span className="tfl-dev-expanded-label">Technical Details</span>
                                    <pre>{detailsText}</pre>
                                  </div>
                                )}
                                <button
                                  className="tfl-dev-copy-btn"
                                  onClick={() =>
                                    handleCopy(
                                      `${line.message}${detailsText ? "\n\n" + detailsText : ""}`,
                                      "Full log copied"
                                    )
                                  }
                                >
                                  <Copy size={12} /> Copy full log
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {pagination?.has_more && (
                      <button className="tfl-load-more-btn" onClick={handleLoadMore} disabled={loadingMore}>
                        <ChevronsDown size={14} className={loadingMore ? "tfl-spin" : ""} />
                        {loadingMore ? "Loading…" : "Load more"}
                      </button>
                    )}
                  </>
                )
              ) : filteredUserEvents.length === 0 ? (
                <div className="tfl-empty-state">
                  <FileText size={28} />
                  <p>
                    {userEvents.length === 0
                      ? "No activity recorded for this day."
                      : "Nothing matches your filters."}
                  </p>
                </div>
              ) : (
                <div className="tfl-user-list">
                  {filteredUserEvents.map((item) => {
                    const expanded = expandedId === item._id;
                    const detailsText = formatTechnicalDetails(item.technical_details);
                    return (
                      <div
                        key={item._id}
                        className="tfl-user-card"
                        style={{ "--chip-color": levelColor(item.level_type) }}
                      >
                        <div className="tfl-user-icon">{item.icon || "•"}</div>
                        <div className="tfl-user-body">
                          <p className="tfl-user-message">{item.message}</p>
                          <span className="tfl-user-time">{item.time}</span>
                          {detailsText && (
                            <button
                              className="tfl-user-details-toggle"
                              onClick={() => setExpandedId(expanded ? null : item._id)}
                            >
                              {expanded ? "Hide details" : "Show details"}
                            </button>
                          )}
                          {expanded && detailsText && (
                            <div className="tfl-user-details-block">
                              <pre>{detailsText}</pre>
                              <button
                                className="tfl-user-copy-btn"
                                onClick={() => handleCopy(detailsText, "Details copied — ready to share")}
                              >
                                <Copy size={12} /> Copy for tech team
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Logs;
